/* EntitySelect — the antd entity dropdown: server-side search, page-on-scroll.
 *
 * docs/design/spec/02-components.md maps the mockups' `.combo` to
 * `<Select showSearch onPopupScroll>`; this is that component, sharing `modelMap`,
 * `EntityDropDownSettings` and `maptoSelect2Objects` with the legacy react-select
 * `EntityDropdown` so option shapes stay identical across both.
 *
 * Why not reuse `EntityDropdown` directly: it renders react-select, which cannot take the
 * `ConfigProvider` theme — a filter row would mix two control kits, two focus rings and two
 * radii on one line. Migrate a screen's dropdowns to this component when that screen is
 * redesigned; do not swap it in under a legacy page.
 *
 * Value contract, deliberately the same as EntityDropdown's so `filterUrl()` keeps working:
 *   isMulti  → an array of full option objects  (`{...entity, value, label}`)
 *   single   → whatever the caller stores; an id or a full object are both accepted in,
 *              and `onChange` always hands back the full option object (or null).
 * A bare id cannot render its own label, so pass `seed` when one is preselected (e.g. the
 * supervisor's own county, which rides on `me` as a full object; an ARRAY of them for a multi
 * select fed from a label cache) — otherwise the first page of options is fetched on mount to
 * resolve it.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Popover, Select, Spin } from "antd";
import { useSelector } from "react-redux";

import { DROPDOWN_PAGE_SIZE, EntityDropDownSettings, FILTER_DEBOUNCE_MS, modelMap } from "../config/config";
import { ENTITY_DROPDOWN_SETTINGS } from "../config/lang";
import { call } from "../util/action";
import { maptoSelect2Objects, translate } from "../util/util";
import { initialsOf } from "./table-cells";
import "./entity-select.scss";

/** How close to the bottom of the option list counts as "load the next page". */
const SCROLL_SLACK = 48;

/** Rows the `+N` popover lists before it gives up and counts the rest. */
const REST_POPOVER_MAX = 30;

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);

const labelOf = o => {
  if (!o || typeof o !== "object") return String(o ?? "");
  return o.label || o.name || [o.first_name, o.last_name].filter(Boolean).join(" ") || String(o.id ?? "");
};

/** antd wants `{value,label}`; we keep the full entity object beside it for `filterUrl`. */
const toAntd = o => ({ value: idOf(o), label: labelOf(o) });

export default function EntitySelect({
  entity,
  value,
  onChange,
  isMulti = false,
  seed = null,
  county,
  moduleFilter,
  extraQuery,
  prefix,
  placeholder,
  disabled,
  allowClear = true,
  className = "",
  id,
  /* Chip overflow. A tight filter row keeps the design's one-chip-plus-`+N`; a full-width form
     field passes `"responsive"` so it shows as many chips as fit.
     ⚠ NO destructuring default here, deliberately. It used to be `maxTagCount = 1`, which meant
     `maxTagCount={undefined}` — precisely what a caller passes to say "no limit" — landed back on
     1. Every `tall` field, including the one built to show a whole group's points in a scrollable
     area, collapsed to one chip plus `+N` with the tall box's extra height sitting empty
     (PICSELL-1574 #34, #23). A default cannot express "unlimited"; `tagLimit` below can. */
  maxTagCount,
  /* A tall, wrapping, scrollable chip area (a group's point list) instead of the default one-row
     control. Overrides the nowrap chrome in `entity-select.scss`, and defaults the chip limit to
     unlimited — showing every chip is the whole reason for the taller box. */
  tall = false,
  /* For a select with no visible <label> of its own — every field needs an accessible name. */
  ariaLabel,
  /* The id of a hint under the field, so a screen reader reads it with the control. */
  ariaDescribedBy,
  /* antd's validation state ("error" / "warning"), for a control that is not inside a `<Form.Item>`
     — an inline field editor marks its own refusal. */
  status,
  /* A person picker (single select): the value and every option row lead with the house avatar —
     photo preferred, initials otherwise — as `PersonCell` draws a person in a table. */
  avatar = false,
  /**
   * Called with the full option objects once bare ids have been resolved to labels.
   *
   * A shared or bookmarked URL carries ids only, so a caller that restores its state from one
   * holds `{ id: 52 }` and can only render "52". This component already fetches page 1 to name
   * such a value for its own display; the callback hands that work back rather than making every
   * caller repeat the request.
   */
  onResolve,
}) {
  const lang = useSelector(state => state.me?.language) || "en";
  const t = key => translate(ENTITY_DROPDOWN_SETTINGS, key, lang);

  const [options, setOptions] = useState([]);
  /* ⚠ Every option ever seen in this scope, by id. `options` holds only the CURRENT page/query and a
     search replaces it wholesale, so resolving a selected id against `options` alone meant that
     typing a new search term turned the existing chips into their raw ids — three chosen points
     became "10 +2", which reads as the selection having been lost (PICSELL-1574 #22, #31). Labels
     have to outlive the option list that supplied them. Kept in state, not a ref, because a chip
     re-renders when a name arrives; `remember` returns the previous Map untouched when nothing is
     new, so it cannot loop. */
  const [known, setKnown] = useState(() => new Map());
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  /* How many rows exist server-side, for the panel's `loaded / total` counter — the design's
     `.combo__foot`, which is what tells the user the list is paged rather than short. */
  const [total, setTotal] = useState(0);
  const page = useRef(1);
  const term = useRef("");
  /* Only the newest request may paint: a fast typist otherwise gets an older page's
     options landing on top of the current query's. */
  const seq = useRef(0);
  const debounce = useRef(null);
  const seeded = useRef(false);

  const scope = `${entity}|${county ?? ""}|${moduleFilter ?? ""}|${JSON.stringify(extraQuery || null)}`;

  const remember = useCallback(list => {
    setKnown(prev => {
      let next = prev;
      list.forEach(o => {
        const key = String(idOf(o));
        if (key && key !== "undefined" && !next.has(key)) {
          if (next === prev) next = new Map(prev);
          next.set(key, o);
        }
      });
      return next;
    });
  }, []);

  const buildUrl = useCallback(
    (nextPage, query) => {
      const base = String(modelMap[entity] || entity).replace(/\/+$/, "");
      let url = `${base}?page=${nextPage}&page_size=${DROPDOWN_PAGE_SIZE}`;
      if (query) url += `&search=${encodeURIComponent(query)}`;
      // Points are scoped by the region and network filters that sit beside them.
      if (entity === "point" || entity === "points") url += `&locality__county=${county || ""}&network=${moduleFilter || ""}`;
      Object.entries(extraQuery || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") url += `&${k}=${encodeURIComponent(v)}`;
      });
      return url;
    },
    [entity, county, moduleFilter, extraQuery]
  );

  const fetchPage = useCallback(
    (nextPage, query, append) => {
      const mine = ++seq.current;
      setLoading(true);
      call({ method: "GET", url: buildUrl(nextPage, query) }, response => {
        if (mine !== seq.current) return;
        const rows = Array.isArray(response) ? response : response?.results || [];
        const mapped = maptoSelect2Objects(rows.slice(), entity) || [];
        page.current = nextPage;
        setHasMore(Array.isArray(response) ? false : Boolean(response?.next));
        setTotal(Array.isArray(response) ? response.length : response?.count ?? 0);
        remember(mapped);
        setOptions(prev => {
          const byValue = new Map((append ? prev : []).map(o => [o.value, o]));
          mapped.forEach(o => byValue.set(o.value, o));
          return Array.from(byValue.values());
        });
        setLoading(false);
      });
    },
    [entity, buildUrl, remember]
  );

  /* Names a preselected id WITHOUT touching the visible option list.
     ⚠ This used to be `fetchPage(1, "", false)`, which replaced `options` with unfiltered page 1 —
     so the moment a chip could not name itself, an active search's results were wiped out and the
     search looked broken (PICSELL-1574 #22). It also bumped `seq`, cancelling the in-flight query.
     Feeding only the label cache keeps the two concerns apart. */
  const resolveNames = useCallback(() => {
    call({ method: "GET", url: buildUrl(1, "") }, response => {
      const rows = Array.isArray(response) ? response : response?.results || [];
      remember(maptoSelect2Objects(rows.slice(), entity) || []);
    });
  }, [entity, buildUrl, remember]);

  /* A scope change (region → points, network → points) invalidates the loaded list; keeping it
     would serve the previous scope's options until the user scrolled. */
  useEffect(() => {
    setOptions([]);
    // Cleared with the options: ids are only unique per entity, so a cached label from the previous
    // scope could name the wrong record.
    setKnown(new Map());
    setHasMore(true);
    setTotal(0);
    page.current = 1;
    term.current = "";
    seeded.current = false;
  }, [scope]);

  /* Resolve a preselected value to a label. A full object is self-describing; `seed` covers a
     bare id without a request (the supervisor's own county); anything still unresolved falls
     back to showing the raw id and triggers one page-1 fetch to try to name it. That last case
     is how a shared/bookmarked URL arrives — it carries ids only, so a value outside the first
     page of options (points, products) stays as its id. Filtering is still correct. */
  const selected = useMemo(() => {
    const list = isMulti ? (Array.isArray(value) ? value : []) : value === "" || value === null || value === undefined ? [] : [value];
    return list.map(v => {
      if (v && typeof v === "object" && (v.label || v.name || v.first_name || v.last_name)) return v;
      const key = idOf(v);
      const fromSeed = [].concat(seed || []).find(s => s && String(idOf(s)) === String(key));
      if (fromSeed) return fromSeed;
      return known.get(String(key)) || { id: key, label: String(key), unresolved: true };
    });
  }, [value, isMulti, seed, known]);

  const hasUnresolved = selected.some(o => o.unresolved);
  useEffect(() => {
    if (hasUnresolved && !seeded.current) {
      seeded.current = true;
      resolveNames();
    }
  }, [hasUnresolved, resolveNames]);

  /* Report the names back up. The signature guard is what keeps this from looping: a caller that
     stores the label re-renders this component, which recomputes `selected` to the same values —
     without it, "report → parent state → re-render → report" would never settle. */
  const reported = useRef("");
  useEffect(() => {
    if (!onResolve) return;
    const named = selected.filter(o => o && !o.unresolved && labelOf(o));
    if (!named.length) return;
    // \u0000 / \u0001 as separators, written as ESCAPES not literal bytes: a raw NUL in the
    // source makes the whole file test as binary, and grep then skips it silently on every
    // repo-wide search.
    const signature = named.map(o => `${idOf(o)}\u0000${labelOf(o)}`).join("\u0001");
    if (signature === reported.current) return;
    reported.current = signature;
    onResolve(named);
  }, [selected, onResolve]);

  const handleOpen = open => {
    if (open && !options.length && !loading) fetchPage(1, term.current, false);
  };

  const handleSearch = query => {
    term.current = query;
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchPage(1, query, false), FILTER_DEBOUNCE_MS);
  };

  const handleScroll = event => {
    const el = event.currentTarget;
    if (loading || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_SLACK) fetchPage(page.current + 1, term.current, true);
  };

  /* antd hands back its own `{value,label}` plus the option object it came from; the caller
     needs the entity object (it carries `code` / `name`, which `filterUrl` reads). */
  const handleChange = (next, option) => {
    /* ⚠ Reconstruct from `next` rather than filtering `option`. rc-select hands back `{}` for an
       entry it could not match to a loaded option, so `.map(o => o.entity).filter(Boolean)` DROPPED
       that selection outright — a silent value loss, worst exactly where the labels were already
       unresolved. `next` always carries `{value,label}` (labelInValue), so an unmatched entry can
       still be preserved by id and keep the label rc-select cached for it. */
    const fromValue = v => {
      const key = String(idOf(v));
      return known.get(key) || { id: idOf(v), label: v?.label ?? key };
    };
    if (isMulti) {
      const picked = Array.isArray(option) ? option : [];
      const values = Array.isArray(next) ? next : [];
      onChange(values.map((v, i) => picked[i]?.entity || fromValue(v)));
      return;
    }
    if (next === undefined || next === null) {
      onChange(null);
      return;
    }
    onChange(option?.entity || fromValue(next));
  };

  /* Selected rows are merged in so a value chosen on an earlier page still labels itself, and
     so the tick renders — antd matches by `value`, not by object identity. */
  const antdOptions = useMemo(() => {
    const byValue = new Map();
    selected.forEach(o => byValue.set(idOf(o), { ...toAntd(o), entity: o }));
    options.forEach(o => byValue.set(o.value, { ...toAntd(o), entity: o }));
    return Array.from(byValue.values());
  }, [options, selected]);

  const antdValue = isMulti ? selected.map(toAntd) : selected.length ? toAntd(selected[0]) : undefined;

  /* An explicit prop wins; otherwise a tall control shows every chip and a normal one shows one. */
  const tagLimit = maxTagCount !== undefined ? maxTagCount : tall ? undefined : 1;

  /* The matched run is marked, as in the design. Searching is the point of a 600-row list, and
     without it the user has to re-read every row to see why it came back. */
  const highlight = label => {
    const query = term.current.trim();
    if (!query) return label;
    const at = label.toLowerCase().indexOf(query.toLowerCase());
    if (at < 0) return label;
    return (
      <>
        {label.slice(0, at)}
        <mark>{label.slice(at, at + query.length)}</mark>
        {label.slice(at + query.length)}
      </>
    );
  };

  /* The avatar's font size is inline on purpose — antd writes its own inline (see `PersonCell`). */
  const person = (entityRow, label) => {
    const name = labelOf(entityRow) || String(label ?? "");
    return (
      <span className="es-person">
        <Avatar size={24} className="lp-who__av es-person__av" style={{ fontSize: 12 }} src={entityRow?.photo || null}>
          {initialsOf(name)}
        </Avatar>
        <span className="es-person__name">{label}</span>
      </span>
    );
  };

  return (
    <Select
      id={id}
      status={status}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      className={`es ${tall ? "es--tall" : ""} ${className}`}
      /* `popupClassName` is deprecated in antd v6; the popup's classes live under `classNames`. */
      classNames={{ popup: { root: "es-panel" } }}
      mode={isMulti ? "multiple" : undefined}
      labelInValue
      showSearch
      // Search runs on the server; filtering the fetched page again would hide valid rows.
      filterOption={false}
      allowClear={allowClear}
      /* Defaults to one chip plus a `+N` (the design's filter-row behaviour — see the note in
         `entity-select.scss`); a wide form field overrides to `"responsive"`. */
      maxTagCount={tagLimit}
      /* antd's default rest label is `+ N ...`; the design's is a bare `+N`. It carried the hidden
         values on a `title` attribute only, which is a slow reveal on a mouse and no reveal at all
         on a touch screen — so "there is no way to see what is actually selected" (PICSELL-1574
         #34, #23). rc-select renders this as a plain span with no popover of its own, so the
         affordance has to be added here. */
      maxTagPlaceholder={rest => {
        const hidden = rest.map(r => r.label).join(", ");
        return (
          <Popover
            trigger={["hover", "click"]}
            content={
              <div className="es-rest">
                {rest.slice(0, REST_POPOVER_MAX).map(r => (
                  <div className="es-rest__row" key={String(r.value)}>
                    {r.label}
                  </div>
                ))}
                {rest.length > REST_POPOVER_MAX && <div className="es-rest__more">+{rest.length - REST_POPOVER_MAX}</div>}
              </div>
            }>
            {/* The click must not also open the dropdown — reading the selection and editing it are
                two different intents. */}
            <span title={hidden} aria-label={hidden} onClick={event => event.stopPropagation()}>
              +{rest.length}
            </span>
          </Popover>
        );
      }}
      // 264px, the design's `.combo__list` — a little over seven rows, so the list reads as scrollable.
      listHeight={264}
      disabled={disabled}
      value={antdValue}
      options={antdOptions}
      optionRender={option => (avatar ? person(option.data?.entity, highlight(String(option.label ?? ""))) : highlight(String(option.label ?? "")))}
      labelRender={
        avatar ? ({ value: picked, label }) => person(selected.find(o => String(idOf(o)) === String(picked)) || known.get(String(picked)), label) : undefined
      }
      loading={loading}
      notFoundContent={
        <div className="es-row">
          {loading ? <Spin size="small" /> : null}
          {t(loading ? "option_loading" : "option_no_matches")}
        </div>
      }
      /* The design's panel foot: how much of the list is loaded, and whether scrolling brings
         more. Without it a 20-of-620 list looks like a 20-row list. */
      popupRender={menu => (
        <>
          {menu}
          {options.length > 0 && (
            <div className="es-foot">
              <span className="es-foot__count">
                {options.length} / {total || options.length}
              </span>
              <span>{hasMore ? t("option_scroll_more") : null}</span>
            </div>
          )}
        </>
      )}
      placeholder={placeholder || t(EntityDropDownSettings[entity]?.selectTitle) || undefined}
      prefix={prefix}
      onOpenChange={handleOpen}
      onSearch={handleSearch}
      onPopupScroll={handleScroll}
      onChange={handleChange}
    />
  );
}
